/**
 * Repository Card Component
 * Migrated to use cambrian-ds Card and design tokens
 */

import { Card, CardBody, Chip } from "cambrian-ds";
import type { ProjectRepository } from '../types/project';
import { cn } from "@/lib/utils";
import { cambrianColors, typography, spacing } from "@/styles/design-tokens";

interface RepoCardProps {
  repo: ProjectRepository;
}

export function RepoCard({ repo }: RepoCardProps) {
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const languageColors: Record<string, string> = {
    TypeScript: 'bg-blue-100 text-blue-800',
    JavaScript: 'bg-yellow-100 text-yellow-800',
    Python: 'bg-green-100 text-green-800',
    Go: 'bg-cyan-100 text-cyan-800',
    Rust: 'bg-orange-100 text-orange-800',
    Solidity: 'bg-purple-100 text-purple-800',
    'C++': 'bg-pink-100 text-pink-800',
  };

  return (
    <Card className={cn(
      cambrianColors.bg.surface,
      cambrianColors.border.primary,
      "hover:shadow-elevation-medium transition-all"
    )}>
      <CardBody className="p-5">
      {/* Header with repo name and stars */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <a
              href={repo.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                typography.h4,
                cambrianColors.brand.primary,
                "hover:text-cambrian-brand-hover dark:hover:text-green-500 transition-colors inline-flex items-center gap-2 group"
              )}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              <span className="truncate">{repo.owner}/{repo.name}</span>
              <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            {repo.isOfficial && (
              <Chip
                variant="solid"
                size="sm"
                className={cn(cambrianColors.brand.bg, "text-white flex-shrink-0")}
              >
                Official
              </Chip>
            )}
          </div>
        </div>
        <div className={cn("flex items-center gap-1 ml-3 flex-shrink-0", cambrianColors.text.secondary)}>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className={cn(typography.body, "font-medium")}>{repo.stars.toLocaleString()}</span>
        </div>
      </div>

      {/* Description */}
      {repo.description && (
        <p className={cn(typography.body, cambrianColors.text.secondary, "mb-3 line-clamp-2")}>
          {repo.description}
        </p>
      )}

      {/* Metadata */}
      <div className={cn("flex flex-wrap items-center", spacing.sm, typography.small, cambrianColors.text.muted)}>
        {repo.language && (
          <Chip
            variant="flat"
            size="sm"
            className={languageColors[repo.language] || 'bg-gray-100 text-gray-800'}
          >
            {repo.language}
          </Chip>
        )}
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Updated {formatDate(repo.pushedAt || repo.updatedAt)}
        </span>
        {repo.contractCount > 0 && (
          <span className={cn("flex items-center gap-1 font-medium", cambrianColors.brand.primary)}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {repo.contractCount} {repo.contractCount === 1 ? 'contract' : 'contracts'}
          </span>
        )}
        {repo.chains.length > 0 && (
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            {repo.chains.join(', ')}
          </span>
        )}
      </div>
      </CardBody>
    </Card>
  );
}
